"use client";

import { DatePicker as HeroDatePicker } from "@heroui/react";
import { CalendarDate, DateValue } from "@internationalized/date";
import { I18nProvider } from "@react-aria/i18n";
import { useLocale } from "next-intl";
import { useMemo } from "react";

interface DatePickerProps {
  /** The label displayed above the picker */
  label?: string;
  /** Current value */
  value: Date | null;
  /** Called when the date changes */
  onChange: (value: Date | null) => void;
  /** Minimum selectable date */
  minDate?: Date;
  /** Maximum selectable date */
  maxDate?: Date;
  /** Whether the picker is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Additional class names for the wrapper */
  className?: string;
  /** Description text below the input */
  description?: string;
  /** Function to determine if a date should be disabled */
  isDateDisabled?: (date: Date) => boolean;
}

/**
 * Convert a native Date to a CalendarDate (date-only, no time)
 */
function dateToCalendarDate(date: Date): CalendarDate {
  return new CalendarDate(
    date.getFullYear(),
    date.getMonth() + 1, // CalendarDate months are 1-indexed
    date.getDate(),
  );
}

/**
 * Convert a CalendarDate to a native Date (at midnight local time)
 */
function calendarDateToDate(calendarDate: CalendarDate): Date {
  return new Date(calendarDate.year, calendarDate.month - 1, calendarDate.day);
}

export function DatePicker({
  label,
  value,
  onChange,
  minDate,
  maxDate,
  disabled = false,
  required = false,
  className = "",
  description,
  isDateDisabled,
}: DatePickerProps) {
  const locale = useLocale();

  // Convert locale to BCP 47 format (fi -> fi-FI, en -> en-US)
  const bcp47Locale = locale === "fi" ? "fi-FI" : "en-US";

  // Convert native Date to CalendarDate for HeroUI
  const calendarValue = useMemo(() => {
    if (!value) return null;
    return dateToCalendarDate(value);
  }, [value]);

  // Convert minDate to CalendarDate
  const minValue = useMemo(() => {
    if (!minDate) return undefined;
    return dateToCalendarDate(minDate);
  }, [minDate]);

  // Convert maxDate to CalendarDate
  const maxValue = useMemo(() => {
    if (!maxDate) return undefined;
    return dateToCalendarDate(maxDate);
  }, [maxDate]);

  // Handle change - convert CalendarDate back to native Date
  const handleChange = (newValue: CalendarDate | null) => {
    if (!newValue) {
      onChange(null);
      return;
    }
    onChange(calendarDateToDate(newValue));
  };

  // Convert isDateDisabled to work with DateValue
  const isDateUnavailable = useMemo(() => {
    if (!isDateDisabled) return undefined;
    return (date: DateValue) => {
      // DateValue can be CalendarDate, CalendarDateTime, or ZonedDateTime
      // All have year, month, day properties
      const nativeDate = new Date(date.year, date.month - 1, date.day);
      return isDateDisabled(nativeDate);
    };
  }, [isDateDisabled]);

  return (
    <I18nProvider locale={bcp47Locale}>
      <HeroDatePicker
        label={label}
        value={calendarValue}
        onChange={handleChange}
        minValue={minValue}
        maxValue={maxValue}
        isDisabled={disabled}
        isRequired={required}
        className={className}
        description={description}
        isDateUnavailable={isDateUnavailable}
        showMonthAndYearPickers
        granularity="day"
      />
    </I18nProvider>
  );
}

export default DatePicker;
